import { STAGES } from "./pipeline";

// One address contract shared by the Pipeline and the Prospects list (Pipeline proposal D-05). Unknown values are ignored; an
// address always wins over what the browser remembers. Pure module, no React: tested in prospectViews.test.js.

export const PHASES = [
  { id: "prospection", label: "Prospection", stageIds: ["new_lead", "contacted", "meeting_scheduled", "qualified", "demo_negotiation"] },
  { id: "activation", label: "Mise en service", stageIds: ["won", "onboarding_in_progress", "active_client"] },
  { id: "exit", label: "Perdus", stageIds: ["lost"] },
  { id: "all", label: "Tout", stageIds: STAGES.map((s) => s.id) },
];
export const PHASE_IDS = PHASES.map((p) => p.id);
export const DEFAULT_PHASE = "all";
export const phaseOf = (id) => PHASES.find((p) => p.id === id) || PHASES[0];

export const SCOPES = [
  { id: "me", label: "Moi" },
  { id: "all", label: "Tous" },
  { id: "none", label: "Non assignés" },
];
export const SORTS = [
  { id: "activity", label: "Activité récente" },
  { id: "name", label: "Nom (A → Z)" },
];
export const FLAGS = [
  { id: "late", label: "À faire aujourd'hui ou en retard", transferable: false },
  { id: "noaction", label: "Aucune action visible", transferable: false },
  { id: "dormant", label: "Inactifs (14 j)", transferable: true },
];
export const flagLabel = (id) => FLAGS.find((f) => f.id === id)?.label || id;

const oneOf = (value, allowed) => (allowed.includes(value) ? value : undefined);
const text = (value, max = 200) => {
  const v = (value || "").trim();
  return v ? v.slice(0, max) : undefined;
};

// Reads every key of the contract from URLSearchParams; a missing or unknown value is simply absent from the result.
export function parseView(params) {
  const get = (k) => params.get(k) ?? undefined;
  const view = {
    q: text(get("q")),
    scope: oneOf(get("scope"), SCOPES.map((s) => s.id)),
    phase: oneOf(get("phase"), PHASE_IDS),
    pilot: oneOf(get("pilot"), ["yes", "no"]),
    channel: text(get("channel"), 60),
    flag: oneOf(get("flag"), FLAGS.map((f) => f.id)),
    sort: oneOf(get("sort"), SORTS.map((s) => s.id)),
    assignee: text(get("assignee"), 64),
    fiche: text(get("fiche"), 64),
  };
  return Object.fromEntries(Object.entries(view).filter(([, v]) => v !== undefined));
}

// Writes a view back as an address, leaving out what equals the default so addresses stay short.
export function serializeView(view, defaults = {}) {
  const out = new URLSearchParams();
  for (const [key, value] of Object.entries(view)) {
    if (value === undefined || value === null || value === "" || defaults[key] === value) continue;
    out.set(key, value);
  }
  return out;
}

// --- Pipeline -> Prospects ---------------------------------------------------------------------------------------------------
// Only what the list can really filter on travels; `skipped` names the rest so the list can say so (never silently).
export function pipelineToProspects(view, userId) {
  const params = new URLSearchParams();
  const skipped = [];
  if (view.q) params.set("q", view.q);
  if (view.phase && view.phase !== "all") params.set("phase", view.phase);
  if (view.scope === "me" && userId) params.set("assignee", userId);
  if (view.scope === "none") params.set("assignee", "__none__");
  if (view.assignee && view.scope !== "none") params.set("assignee", view.assignee);
  if (view.pilot) params.set("pilot", view.pilot);
  if (view.channel) params.set("channel", view.channel);
  if (view.flag) {
    if (FLAGS.find((f) => f.id === view.flag)?.transferable) params.set("flag", view.flag);
    else skipped.push(view.flag);
  }
  if (skipped.length) params.set("skipped", skipped.join(","));
  return params;
}

// Prospects -> Pipeline: what the board can filter on. `skipped` carries the list's own filters the board has no equivalent for.
export function prospectsToPipeline(list, userId) {
  const params = new URLSearchParams();
  const skipped = [];
  if (list.q) params.set("q", list.q);
  if (list.phase) params.set("phase", list.phase);
  else if (list.status) {
    const phase = PHASES.find((p) => p.id !== "all" && p.stageIds.includes(list.status));
    params.set("phase", phase ? phase.id : "all");
    skipped.push("status");
  }
  if (list.assignee === "__none__") params.set("scope", "none");
  else if (list.assignee && list.assignee === userId) params.set("scope", "me");
  else if (list.assignee) params.set("assignee", list.assignee);
  if (list.pilot === "yes" || list.pilot === "no") params.set("pilot", list.pilot);
  if (list.channel) params.set("channel", list.channel);
  if (list.flag === "dormant") params.set("flag", "dormant");
  if (list.region) skipped.push("region");
  if (list.source) skipped.push("source");
  if (skipped.length) params.set("skipped", skipped.join(","));
  return params;
}

// --- Memory (per browser and per account) ------------------------------------------------------------------------------------
export const memoryKey = (userId) => `jamora.pipeline.v2:${userId}`;

export function sanitizeMemory(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out = {};
  if (PHASE_IDS.includes(raw.phase)) out.phase = raw.phase;
  if (SCOPES.some((s) => s.id === raw.scope)) out.scope = raw.scope;
  if (SORTS.some((s) => s.id === raw.sort)) out.sort = raw.sort;
  if (raw.columns && typeof raw.columns === "object" && !Array.isArray(raw.columns)) {
    const columns = {};
    for (const s of STAGES) if (typeof raw.columns[s.id] === "boolean") columns[s.id] = raw.columns[s.id];
    if (!STAGES.every((s) => columns[s.id] === false)) out.columns = columns; // never remember « every column hidden »
  }
  return out;
}

export function readMemory(userId, storage = globalThis.localStorage) {
  if (!userId) return {};
  try {
    const raw = storage.getItem(memoryKey(userId));
    return sanitizeMemory(raw ? JSON.parse(raw) : null);
  } catch (e) {
    return {};
  }
}

export function writeMemory(userId, memory, storage = globalThis.localStorage) {
  if (!userId) return;
  try {
    storage.setItem(memoryKey(userId), JSON.stringify(sanitizeMemory(memory)));
  } catch (e) {
    /* private mode / quota: the choice just isn't remembered */
  }
}

// Columns shown in « Tout »: every stage unless the person hid some (a stage absent from the memory stays visible).
export function visibleColumns(phase, columns = {}) {
  const ids = phaseOf(phase).stageIds;
  if (phase !== "all") return ids;
  const shown = ids.filter((id) => columns[id] !== false);
  return shown.length ? shown : ids;
}

// The view actually used: address first, then the account's memory, then defaults. `canScopeAll` guards the scope: without it only « me ».
export function resolveView(urlView, memory, { canScopeAll }) {
  const phase = urlView.phase || memory.phase || DEFAULT_PHASE;
  const wanted = urlView.scope || memory.scope;
  const scope = canScopeAll ? wanted || "all" : "me";
  return { ...urlView, phase, scope, sort: urlView.sort || memory.sort || "activity", hasScope: !!wanted };
}
